import type { DocSection } from "./legalDocUtils";

interface LegalDocPreviewProps {
  title: string;
  subtitle?: string;
  sections: DocSection[];
}

/**
 * Renders the exact same title/subtitle/sections data passed to
 * generateLegalDoc / generateLegalDocPdf as a live, read-only preview --
 * so what's shown here is guaranteed to match what downloads, since it's
 * the same data, not a separately-maintained approximation.
 *
 * Used to call useLogToolUsage(subtitle, undefined, { ownActions: true }) here -- removed, not just left
 * alone, once each of the 8 consumers' actual download buttons got
 * proper logUsageNow() gating: the preview was never the thing being
 * paid for, the downloaded document is, and keeping both would have
 * double-counted one real download as two uses (view the preview, then
 * download it) in whatever tools still called this with a defined
 * subtitle on mount.
 */
export function LegalDocPreview({ title, subtitle, sections }: LegalDocPreviewProps) {
  return (
    <div className="border rounded-lg bg-muted/20 p-3">
      <p className="text-xs font-medium text-muted-foreground mb-2 px-1">Preview</p>
      <div className="bg-white text-black rounded-md shadow-sm p-8 max-h-[500px] overflow-y-auto text-sm leading-relaxed">
        <h1 className="text-xl font-bold text-center mb-1">{title || "Untitled Document"}</h1>
        {subtitle && <p className="text-base font-semibold text-center mb-4">{subtitle}</p>}
        {sections.map((section, i) => (
          <div key={i} className="mt-4 first:mt-4">
            {section.heading && <h2 className="text-base font-bold mb-1.5">{section.heading}</h2>}
            {section.paragraphs.map((para, j) => (
              <p key={j} className="mb-2 whitespace-pre-wrap">{para || " "}</p>
            ))}
            {section.bullets && (
              <ul className="list-disc pl-5 space-y-1 mb-2">
                {section.bullets.map((bullet, k) => <li key={k}>{bullet}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
